import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { AlertTriangle, RefreshCw, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// DataFreshnessBanner — surfaces the most recent month that has actuals data
// on a given page. Kat's biggest frustration was "why is June missing?" — the
// answer is usually "the source sheet hasn't been updated yet for that month."
// Now also exposes an admin-only "Sync from Sheet now" button so an admin can
// re-pull QuickBooks numbers the moment the sheet is refreshed instead of
// waiting for the scheduled daily sync.
//
// Props:
//   kind: 'actuals' | 'pnl_buckets' | 'sales'
//   label: friendly source name (e.g. 'Indie Financials sheet')
export default function DataFreshnessBanner({ kind = 'actuals', label }) {
  const { user } = useAuth();
  const [state, setState] = useState({ loading: true, latestMonth: null, expected: null, lastSynced: null });
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    try {
      let latestMonth = null;
      let lastSynced = null;
      if (kind === 'actuals' || kind === 'pnl_buckets') {
        const [act, status] = await Promise.all([
          api.get('/actuals'),
          api.get('/actuals/status').catch(() => ({ data: {} })),
        ]);
        const list = act.data || [];
        if (list.length) latestMonth = list[list.length - 1].month_label;
        lastSynced = status.data?.last_synced || null;
      } else if (kind === 'sales') {
        const r = await api.get('/sales');
        const months = r.data?.months || [];
        const filled = months.filter((m) => m.revenue_actual != null || m.sales_actual != null);
        if (filled.length) {
          const last = filled[filled.length - 1];
          latestMonth = `${last.label} 2026`;
        }
      }
      // Expected latest month = last calendar month.
      const now = new Date();
      const lastCalendar = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const expected = lastCalendar.toLocaleString('en-US', { month: 'short' }) + ' ' + lastCalendar.getFullYear();
      setState({ loading: false, latestMonth, expected, lastSynced });
    } catch {
      setState({ loading: false, latestMonth: null, expected: null, lastSynced: null });
    }
  }, [kind]);

  useEffect(() => { load(); }, [load]);

  const resync = async () => {
    if (kind !== 'actuals' && kind !== 'pnl_buckets') return;
    setSyncing(true);
    try {
      const r = await api.post('/integrations/actuals/sync');
      if (r.data?.ok) {
        toast.success(`Synced ${r.data.months || 0} months of actuals from the sheet.`);
      } else {
        toast.error(`Sync failed: ${r.data?.error || 'unknown error'}`);
      }
      await load();
    } catch (err) {
      toast.error('Sync request failed.');
    } finally {
      setSyncing(false);
    }
  };

  if (state.loading || !state.latestMonth) return null;

  const isStale = state.expected && state.latestMonth &&
    state.latestMonth.trim().toLowerCase() !== state.expected.trim().toLowerCase();
  const canResync = user?.is_admin && (kind === 'actuals' || kind === 'pnl_buckets');
  const syncedAgo = state.lastSynced ? new Date(state.lastSynced).toLocaleString() : null;

  if (!isStale) {
    return (
      <div data-testid="data-freshness-banner" className="text-[11px] text-indie-ink/60 flex items-center gap-2 -mt-2 flex-wrap">
        <CheckCircle2 size={12} className="text-indie-evergreen" />
        <span className="uppercase tracking-wider">Latest actuals</span>
        <span className="text-indie-navy font-medium">{state.latestMonth}</span>
        {label && <span className="text-indie-ink/50">· {label}</span>}
        {syncedAgo && <span className="text-indie-ink/40">· synced {syncedAgo}</span>}
        {canResync && (
          <button
            type="button"
            onClick={resync}
            disabled={syncing}
            data-testid="banner-resync-btn"
            className="ml-2 inline-flex items-center gap-1 border border-indie-navy/25 bg-white px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold hover:bg-indie-cream disabled:opacity-50"
          >
            <RefreshCw size={10} className={syncing ? 'animate-spin' : ''} /> {syncing ? 'Syncing…' : 'Sync now'}
          </button>
        )}
      </div>
    );
  }

  return (
    <div data-testid="data-freshness-banner" className="border-l-4 border-indie-cobalt bg-indie-cobalt/[0.06] px-4 py-2.5 text-xs flex items-start gap-2.5">
      <AlertTriangle className="text-indie-cobalt flex-shrink-0 mt-0.5" size={14} />
      <div className="flex-1">
        <span className="text-indie-navy font-semibold">
          Latest actuals in view: {state.latestMonth}
        </span>
        <span className="text-indie-ink/70">
          {' '}· expected through {state.expected}. {label ? `Once the ${label} is refreshed for the newest month, click Sync from Sheet.` : 'The source sheet hasn\u2019t been updated for the most recent month yet.'}
        </span>
        {syncedAgo && <div className="text-[10px] text-indie-ink/50 mt-1">Last sync attempt: {syncedAgo}</div>}
      </div>
      {canResync && (
        <button
          type="button"
          onClick={resync}
          disabled={syncing}
          data-testid="banner-resync-btn"
          className="shrink-0 inline-flex items-center gap-1 border border-indie-cobalt bg-white text-indie-cobalt px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold hover:bg-indie-cobalt hover:text-white disabled:opacity-50"
        >
          <RefreshCw size={11} className={syncing ? 'animate-spin' : ''} /> {syncing ? 'Syncing…' : 'Sync from Sheet'}
        </button>
      )}
    </div>
  );
}
